import { useRef, useContext, useCallback } from 'react'
import useTracking from '@hooks/useTracking'

import contextBlickTV from '@contexts/blickTV'
import {
  VideoTrackingParams,
  BlickTVContextTrackingDispatchProps,
  ValidPlayerStatus,
  VolatileTrackingData,
  PlayerMode,
  PlayerType,
  UseCommonPropsInputProps,
  UseTrackingOutputProps,
  ChapterTrackingParams,
} from '../../types/darkcove'

import {
  videoImpressionHandler,
  videoPlayHandler,
  videoPlayerSuccessHandler,
  videoBufferingHandler,
  videoAdRequestHandler,
  videoAdStartedHandler,
  videoAdSkippedHandler,
  videoAdEndedHandler,
  videoAdErroredHandler,
  videoProgressHandler,
  videoProgress10SecIntervalHandler,
  videoQuartileHandler,
  videoEndedHandler,
  videoForwardHandler,
  videoBackwardHandler,
  videoErrorHandler,
  videoMuteHandler,
  videoUnmuteHandler,
  videoCaptionsEnableHandler,
  videoCaptionsDisableHandler,
  videoCloseFloatingPlayerHandler,
  videoToFloatingPlayerHandler,
  videoOverlayEnterHandler,
  videoOverlayExitHandler,
  videoOrientationChangeHandler,
  videoChapterClickHandler,
  videoChapterSwipeHandler,
  videoChapterButtonClickHandler,
  videoUnmuteCTAButtonClickHandler,
  videoUnmuteCTAButtonImpressionHandler,
} from './eventMapper'

import { ViewportOrientationValues } from '@contexts/viewport'

import {
  getPlayerStatus,
  extractErrorObj,
  withBlickTVLog,
  transformCaptionsAndAudioValue,
} from './utils'
import { Vtype } from '@components/AppNexus/types'
import { captureException } from '@utils/sentry'

const { BlickTVContext } = contextBlickTV

export type UseVideoTrackingInputProps = Pick<
  UseCommonPropsInputProps,
  | 'isBlickTV'
  | 'position'
  | 'allowFloating'
  | 'autoplay'
  | 'videoId'
  | 'title'
  | 'duration'
  | 'isInVideoOnlyArticle'
  | 'playerRef'
> & {
  playerType: PlayerType
  vtype: Vtype
}

const useVideoTracking = ({
  isBlickTV,
  allowFloating,
  autoplay,
  playerRef,
  position,
  isInVideoOnlyArticle,
  vtype,
  playerType = PlayerType.VOD,
  videoId = '',
  title: videoTitle = '',
  duration = 0,
}: UseVideoTrackingInputProps): UseTrackingOutputProps => {
  const previousTime = useRef<number>(-1)
  const firstProgress = useRef<boolean>(false)
  const passedTime = useRef<number>(0)
  const hasPlayClicked = useRef<boolean>(false)
  const shouldTriggerOnPlaySuccess = useRef<boolean>(true)
  const hasEnded = useRef<boolean>(false)
  const divValue = useRef<number>(1)
  const div10SecIntervalValue = useRef<number>(1)

  const trackImpression = useTracking(videoImpressionHandler)
  const trackPlay = useTracking(videoPlayHandler)
  const trackPlayerSuccess = useTracking(videoPlayerSuccessHandler)
  const trackBuffering = useTracking(videoBufferingHandler)
  const trackAdRequest = useTracking(videoAdRequestHandler)
  const trackAdStarted = useTracking(videoAdStartedHandler)
  const trackAdSkipped = useTracking(videoAdSkippedHandler)
  const trackAdEnded = useTracking(videoAdEndedHandler)
  const trackAdErrored = useTracking(videoAdErroredHandler)
  const trackProgress = useTracking(videoProgressHandler)
  const trackProgress10SecInterval = useTracking(
    videoProgress10SecIntervalHandler
  )
  const trackQuartileCompletion = useTracking(videoQuartileHandler)
  const trackEnded = useTracking(videoEndedHandler)
  const trackForward = useTracking(videoForwardHandler)
  const trackBackward = useTracking(videoBackwardHandler)
  const trackError = useTracking(videoErrorHandler)
  const trackMute = useTracking(videoMuteHandler)
  const trackUnmute = useTracking(videoUnmuteHandler)
  const trackCaptionsEnabled = useTracking(videoCaptionsEnableHandler)
  const trackCaptionsDisabled = useTracking(videoCaptionsDisableHandler)
  const trackCloseFloatingPlayer = useTracking(videoCloseFloatingPlayerHandler)
  const trackToFloatingPlayer = useTracking(videoToFloatingPlayerHandler)
  const trackEnterOverlay = useTracking(videoOverlayEnterHandler)
  const trackExitOverlay = useTracking(videoOverlayExitHandler)
  const trackOrientationChange = useTracking(videoOrientationChangeHandler)
  const trackChapterClick = useTracking(videoChapterClickHandler)
  const trackChapterSwipe = useTracking(videoChapterSwipeHandler)
  const trackChapterButtonClick = useTracking(videoChapterButtonClickHandler)
  const trackUnmuteCTAButtonClick = useTracking(
    videoUnmuteCTAButtonClickHandler
  )
  const trackUnmuteCTAButtonImpression = useTracking(
    videoUnmuteCTAButtonImpressionHandler
  )

  const { isFloating, setDispatchTrackingEvents } = useContext(BlickTVContext)

  const isFloatingBlickTV = isBlickTV && allowFloating
  const isLivestreamInArticle = !isBlickTV && vtype === Vtype.LIVESTREAM

  const trackingParamsRef = useRef<VolatileTrackingData>({
    quartileState: 0,
    secondsWatched: 0,
    videoTitle,
  })

  const getVolatileData = useCallback(
    () => ({
      ...trackingParamsRef.current,
    }),
    []
  )

  const getStaticData = useCallback(() => {
    return {
      autoplay:
        // In case of video only article or article with livestream content, or BlickTV
        // the autoplay value is false and we handle autoplay manually on the react side.
        // In that case we should send autoplay = 1 within the tracking video events.
        autoplay || isInVideoOnlyArticle || isLivestreamInArticle || isBlickTV
          ? 1
          : 0,
      duration,
      playerType,
      videoId,
      ...(isBlickTV ? { position } : {}),
      ...(isFloatingBlickTV ? { floating: isFloating() } : {}),
    }
  }, [
    autoplay,
    duration,
    isBlickTV,
    isFloating,
    isFloatingBlickTV,
    isInVideoOnlyArticle,
    isLivestreamInArticle,
    playerType,
    position,
    videoId,
  ])

  const getAllData = (
    playerStatus: ValidPlayerStatus | null
  ): VideoTrackingParams => ({
    ...getVolatileData(),
    ...getStaticData(),
    ...transformCaptionsAndAudioValue(
      playerStatus
        ? playerStatus
        : {
            audio: false,
            captions: false,
            playerMode: PlayerMode.INLINE,
          }
    ),
  })

  const getVideoTrackingParameters = (options?: {
    withoutPlayerStatus?: boolean
  }): VideoTrackingParams => {
    const { withoutPlayerStatus } = options || {}
    const playerStatus = withoutPlayerStatus
      ? null
      : getPlayerStatus({
          isFloatingBlickTV,
          playerRef,
          setDispatchTrackingEvents,
          createDispatchFutureStateFunction,
        })

    return getAllData(playerStatus)
  }

  const createDispatchFutureStateFunction =
    (playerState: ValidPlayerStatus) =>
    ({ action }: BlickTVContextTrackingDispatchProps) => {
      let trackAction
      let floating

      if (action === 'popOut') {
        trackAction = trackToFloatingPlayer
        floating = true
      }

      if (action === 'dismiss') {
        trackAction = trackCloseFloatingPlayer
        floating = false
      }

      if (typeof trackAction === 'function') {
        trackAction({
          ...getAllData(playerState),
          floating: floating ?? getStaticData().floating,
        })
      }
    }

  // video-impression
  const onImpression = withBlickTVLog(function onImpression() {
    trackImpression({
      ...getAllData({
        audio: false,
        captions: false,
        playerMode: PlayerMode.INLINE,
      }),
    })
    // TODO: looks like we dont need it
    // if (isFloatingBlickTV) {
    //   setDispatchTrackingEvents(
    //     createDispatchFutureStateFunction({
    //       audio: false,
    //       captions: false,
    //       playerMode: PlayerMode.INLINE,
    //     })
    //   )
    // }
  })

  // video_play_click
  const onPlay = withBlickTVLog(function onPlay() {
    if (!hasPlayClicked.current && !isBlickTV && !autoplay) {
      hasPlayClicked.current = true

      trackPlay({
        ...getVideoTrackingParameters(),
      })
    }
  })

  const onPlaySuccess = withBlickTVLog(function onPlaySuccess() {
    if (shouldTriggerOnPlaySuccess.current) {
      shouldTriggerOnPlaySuccess.current = false

      trackPlayerSuccess({
        ...getVideoTrackingParameters(),
      })
    }
  })

  // video_buffering
  const onBuffering = withBlickTVLog(function onBuffering() {
    trackBuffering({
      ...getVideoTrackingParameters(),
    })
  })

  // video_ads_ad_request
  const onAdRequest = withBlickTVLog(function onAdRequest() {
    trackAdRequest({
      ...getVideoTrackingParameters(),
    })
  })

  // video_ads_ad_started
  const onAdStarted = withBlickTVLog(function onAdStarted() {
    trackAdStarted({
      ...getVideoTrackingParameters(),
    })
  })

  // video_ads_ad_skip
  const onAdSkipped = withBlickTVLog(function onAdSkipped() {
    trackAdSkipped({
      ...getVideoTrackingParameters(),
    })
  })

  // video_ads_ad_end
  const onAdEnded = withBlickTVLog(function onAdEnded() {
    trackAdEnded({
      ...getVideoTrackingParameters(),
    })
  })

  // video_ads_ad_error
  const onAdErrored = withBlickTVLog(function onAdErrored() {
    trackAdErrored({
      ...getVideoTrackingParameters(),
    })
  })

  const onSeek = withBlickTVLog(function onSeek({
    currentTime,
  }: {
    currentTime: number
  }) {
    const currentTimeInt = Math.ceil(currentTime)

    // resetting passedTime/previousTime/divValue when user seek to past or future
    // to calculate correctly 'currentSecondsWatched' for 'video_watched' event
    // (see onProgress) and avoid negative values
    if (currentTimeInt > 0) {
      passedTime.current = currentTimeInt
      previousTime.current = -1
      divValue.current = 1
      div10SecIntervalValue.current = 1
    }
  })

  // video_watched
  const onProgress = withBlickTVLog(function onProgress({
    currentTime,
  }: {
    currentTime: number
  }) {
    const currentTimeInt = Math.ceil(currentTime)

    if (!firstProgress.current) {
      //* In case we are in DVR mode (only Blick TV)
      if (currentTimeInt > 0) {
        passedTime.current = currentTimeInt
      }
      firstProgress.current = true
    }

    const currentSecondsWatched = currentTimeInt - passedTime.current

    if (previousTime.current < currentSecondsWatched) {
      previousTime.current = currentSecondsWatched

      trackingParamsRef.current.secondsWatched = currentSecondsWatched

      //! Because the event is not always triggered on *every* second
      //! and we only deal with Integer seconds, it might be possible
      //! that we jump from e.g. modulo 14 to modulo 1, and we miss
      //! modulo 0, in which case we wanted to trigger the event.
      //! In that case, we try to catch this by doing a *Float division*
      //! and checking if the result is strictly bigger than the expected
      //! integer if we would have made an *Integer division*.
      if (
        currentSecondsWatched !== 0 &&
        (currentSecondsWatched % 15 === 0 ||
          currentSecondsWatched / 15.0 > divValue.current)
      ) {
        divValue.current = Math.floor(currentSecondsWatched / 15.0) + 1

        trackProgress({
          ...getVideoTrackingParameters(),
        })
      }

      /** video_watched_10 PoC - VoD only */
      if (
        playerType === PlayerType.VOD &&
        currentSecondsWatched !== 0 &&
        (currentSecondsWatched % 10 === 0 ||
          currentSecondsWatched / 10.0 > div10SecIntervalValue.current)
      ) {
        div10SecIntervalValue.current =
          Math.floor(currentSecondsWatched / 10.0) + 1

        trackProgress10SecInterval({
          ...getVideoTrackingParameters(),
        })
      }
    }
  })

  // video_5 | video_25 | video_50 | video_75 | video_95 | video_end
  const onQuartileCompletion = withBlickTVLog(
    function onQuartileCompletion(payload: { percent: number }) {
      trackingParamsRef.current.quartileState = payload.percent

      trackQuartileCompletion({
        ...getVideoTrackingParameters(),
      })
    }
  )

  // video_end
  const onEnd = withBlickTVLog(function onEnd() {
    if (!hasEnded.current) {
      hasEnded.current = true
      trackingParamsRef.current.quartileState = 'end'

      trackEnded({
        ...getVideoTrackingParameters(),
      })
    }
  })

  // video_forward
  const onForward = withBlickTVLog(function onForward() {
    trackForward({
      ...getVideoTrackingParameters(),
    })
  })

  // video_back
  const onBackward = withBlickTVLog(function onBackward() {
    trackBackward({
      ...getVideoTrackingParameters(),
    })
  })

  // video_error
  const onError = withBlickTVLog(function onError(...args) {
    const { data, message, code, type } = extractErrorObj(...args)
    const errorObj = data
      ? {
          errorCode: data[0]?.error_code ?? '',
          errorMessage: data[0]?.message ?? message ?? '',
          errorLog: JSON.stringify({ data, message }),
        }
      : {
          errorCode: type,
          errorMessage: message,
          errorLog: JSON.stringify({ type, message, code }),
        }

    console.error('Video Error:', errorObj)
    captureException(errorObj)

    // Send error to Google Analytics
    trackError({
      ...getVideoTrackingParameters(),
      ...errorObj,
    })
  })

  // video_audio_on
  const onMute = withBlickTVLog(function onMute(params) {
    // dispatch `trackMute` if platform is not web
    // otherwise check if `buttonPressed`
    if (params?.buttonPressed) {
      trackMute({
        ...getVideoTrackingParameters(),
      })
    }
  })

  // video_audio_off
  const onUnmute = withBlickTVLog(function onUnmute(params) {
    if (params?.buttonPressed) {
      trackUnmute({
        ...getVideoTrackingParameters(),
      })
    }
  })

  // video_captions_on
  const onCaptionsEnabled = withBlickTVLog(function onCaptionsEnabled() {
    trackCaptionsEnabled({
      ...getVideoTrackingParameters(),
    })
  })

  // video_captions_off
  const onCaptionsDisabled = withBlickTVLog(function onCaptionsDisabled() {
    trackCaptionsDisabled({
      ...getVideoTrackingParameters(),
    })
  })

  // video_to_overlay
  const onEnterOverlay = withBlickTVLog(function onEnterOverlay() {
    // TODO: looks like we dont need it
    // setDispatchTrackingEvents(
    //   createDispatchFutureStateFunction(playerStatus)
    // )

    trackEnterOverlay({
      ...getVideoTrackingParameters(),
      playerMode: PlayerMode.INLINE,
    })
  })

  // video_close_overlay
  const onExitOverlay = withBlickTVLog(function onExitOverlay() {
    // TODO: looks like we dont need it
    // setDispatchTrackingEvents(
    //   createDispatchFutureStateFunction(playerStatus)
    // )

    trackExitOverlay({
      ...getVideoTrackingParameters(),
      playerMode: PlayerMode.OVERLAY,
    })
  })

  const onOrientationChange = withBlickTVLog(function onOrientationChange(
    orientation: ViewportOrientationValues
  ) {
    trackOrientationChange({
      ...getVideoTrackingParameters(),
      orientation,
    })
  })

  const setNewHeadline = withBlickTVLog(function setNewHeadline(
    headline: string
  ) {
    trackingParamsRef.current.videoTitle = headline
    // TODO: looks like we dont need it
    // if (isFloatingBlickTV) {
    //   setDispatchTrackingEvents(
    //     createDispatchFutureStateFunction(playerStatus)
    //   )
    // }
  })

  const onChapterClick = withBlickTVLog(function onChapterClick(
    chapterEventData: ChapterTrackingParams
  ) {
    // TODO: looks like we dont need it
    // setDispatchTrackingEvents(
    //   createDispatchFutureStateFunction(playerStatus)
    // )

    trackChapterClick({
      ...getVideoTrackingParameters(),
      ...chapterEventData,
    })
  })

  const onChapterSwipe = withBlickTVLog(function onChapterSwipe(
    chapterEventData: ChapterTrackingParams
  ) {
    // TODO: looks like we dont need it
    // setDispatchTrackingEvents(
    //   createDispatchFutureStateFunction(playerStatus)
    // )

    trackChapterSwipe({
      ...getVideoTrackingParameters(),
      ...chapterEventData,
    })
  })

  const onChapterButtonClick = withBlickTVLog(function onChapterButtonClick(
    chapterEventData: ChapterTrackingParams
  ) {
    // TODO: looks like we dont need it
    // setDispatchTrackingEvents(
    //   createDispatchFutureStateFunction(playerStatus)
    // )

    trackChapterButtonClick({
      ...getVideoTrackingParameters(),
      ...chapterEventData,
    })
  })

  const onUnmuteCTAButtonClick = withBlickTVLog(
    function onUnmuteCTAButtonClick() {
      trackUnmuteCTAButtonClick({
        ...getVideoTrackingParameters(),
      })
    }
  )

  const onUnmuteCTAButtonImpression = withBlickTVLog(
    function onUnmuteCTAButtonImpression() {
      trackUnmuteCTAButtonImpression({
        ...getVideoTrackingParameters(),
      })
    }
  )

  return {
    onImpression,
    onPlay,
    onPlaySuccess,
    onBuffering,
    onAdRequest,
    onAdStarted,
    onAdSkipped,
    onAdEnded,
    onAdErrored,
    onProgress,
    onQuartileCompletion,
    onEnd,
    onForward,
    onBackward,
    onSeek,
    onError,
    onMute,
    onUnmute,
    onCaptionsEnabled,
    onCaptionsDisabled,
    onEnterOverlay,
    onExitOverlay,
    onOrientationChange,
    setNewHeadline,
    onChapterClick,
    onChapterSwipe,
    onChapterButtonClick,
    onUnmuteCTAButtonClick,
    onUnmuteCTAButtonImpression,
  }
}

export default useVideoTracking
